import { Affiliate, LoginResponse, NotificationPrivacy, User } from '../types';
import { getAppVersion } from './appVersion';
import { apiFetch } from './apiClient';
import { isTauri } from '../utils/tauri';
import { SERVER_BASE_URL } from '../utils/serverUrl';
import { IS_COARSE_POINTER } from '../utils/pointer';
import {
  buildDeviceBlock,
  DeviceStatusInfo,
  getDeviceIdentity,
  loadResumeIdentity,
  saveResumeIdentity,
} from './deviceIdentity';

// 서버 JWT 만료 정책 분기용 — 모바일(터치 기기, 태블릿 포함)은 30일 토큰 + 슬라이딩 갱신,
// 데스크톱(웹/Tauri)은 12시간. 판별 축은 기기 종류이므로 뷰포트가 아니라 pointer: coarse를 쓴다.
export const getClientType = (): 'mobile' | 'desktop' =>
  !isTauri() && IS_COARSE_POINTER ? 'mobile' : 'desktop';

// 서버 주소 계산은 utils/serverUrl 하나로 통일한다
const API_URL = `${SERVER_BASE_URL}/api`;

// 계열사 목록은 세션 중 변하지 않으므로 promise를 캐시해 동시/중복 호출을 1회 요청으로 dedupe.
// 실패(폴백) 시에는 캐시를 비워 다음 호출에서 재시도한다.
let affiliatesCache: Promise<Affiliate[]> | null = null;

/** JWT payload 디코드(서명 검증 없음 — 서명 대상·화면 분기용. 판정은 서버) */
function decodeTokenClaims(token: string | null): { userid?: string; affiliateId?: string; did?: number; exp?: number } | null {
  if (!token) return null;
  try {
    const part = token.split('.')[1];
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch {
    return null;
  }
}

/** 새 토큰을 저장하고 소켓에도 올린다(socketService가 'auth:token'으로 서버에 전달 — 소켓 스윕이 만료 시각을 본다) */
function storeToken(token: string): void {
  localStorage.setItem('authToken', token);
  window.dispatchEvent(new CustomEvent('auth:token-updated', { detail: token }));
}

export const authService = {
  /**
   * 계열사 목록 조회 (로그인 화면 드롭다운용)
   */
  getAffiliates: (): Promise<Affiliate[]> => {
    if (affiliatesCache) return affiliatesCache;
    affiliatesCache = (async () => {
      try {
        const response = await fetch(`${API_URL}/affiliates`);
        if (!response.ok) throw new Error('Failed to fetch affiliates');
        return await response.json();
      } catch (error) {
        affiliatesCache = null;
        console.warn('Failed to fetch affiliates. Using default.');
        return [{ id: 'INA', label: 'INA 그룹' }];
      }
    })();
    return affiliatesCache;
  },

  /**
   * 사용자 로그인 요청
   */
  login: async (
    userid: string,
    pwd: string,
    affiliateId?: string,
    opts: { requestApproval?: boolean; label?: string } = {}
  ): Promise<LoginResponse> => {
    try {
      const appVersion = await getAppVersion();
      // 기기 신뢰: 이 기기 키로 서버 nonce에 서명해 함께 보낸다(서명 대상 userid는 보내는 문자열 그대로).
      // 키를 못 만들거나 구 서버면 device 없이 보낸다 — 거부 여부는 서버가 정책으로 정한다.
      const device = affiliateId ? await buildDeviceBlock('login', affiliateId, userid, opts) : null;
      const response = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userid, pwd, affiliateId, appVersion, clientType: getClientType(),
          ...(device?.block ? { device: device.block } : {}),
        }),
      });

      const data: LoginResponse = await response.json();
      if (data?.success && data.user) {
        // 토큰이 만료된 뒤 기기 키로 세션을 다시 열 때(resumeSession) 쓸 신원 — DB 아이디 기준.
        // 재개 비밀값은 승인 웹 기기의 로그인에만 온다(없으면 이 기기로는 재개하지 않는다)
        saveResumeIdentity({
          affiliateId: data.user.affiliateId || affiliateId || '',
          userid: data.user.userid || userid,
          ...(typeof data.resumeSecret === 'string' && data.resumeSecret ? { resumeSecret: data.resumeSecret } : {}),
        });
      }
      return data;
    } catch (error) {
      console.error('Backend server not reachable:', error);
      return {
        success: false,
        message: '서버에 연결할 수 없습니다. 네트워크 상태를 확인해주세요.'
      };
    }
  },

  /**
   * 토큰 슬라이딩 갱신 — 앱을 열 때마다 호출해 만료 시각을 뒤로 민다 (모바일 30일 유지의 핵심).
   * 실패(구버전 서버 404, 네트워크 오류)는 조용히 무시하고 기존 토큰으로 계속한다.
   * 401은 apiFetch가 'auth:unauthorized'를 발행해 기존 자동 로그아웃 흐름을 탄다.
   *
   * 응답에 user.avatar가 있으면(신 서버·프로필 조회 성공) 서버 DB의 최신 아바타다 —
   * 소켓이 끊긴 동안 다른 기기에서 바꾼 변경의 유일한 복구 경로(모바일은 재로그인이 없다).
   * avatar === undefined(구 서버·조회 실패)는 "모름"이므로 호출부가 기존 값을 유지해야 하고,
   * null은 "아바타 없음"의 정상 권위 값이다 — 둘을 절대 합치지 말 것.
   *
   * 알림 내용 공개 수준(notificationPrivacy)도 같은 편승·같은 규칙이다. 계정 전체 설정이라
   * 다른 기기에서 바꾼 값이 이 응답으로 복구된다. undefined는 "모름"이므로 기존 값을 유지한다 —
   * 여기서 기본값 'full'로 접으면 DB 장애 한 번에 전 사용자의 숨김 설정이 풀린다.
   */
  refreshToken: async (): Promise<{
    refreshed: boolean;
    avatar?: string | null;
    notificationPrivacy?: NotificationPrivacy;
  }> => {
    try {
      // 기기 신뢰: 승인 기기 토큰은 갱신할 때마다 서명이 필요하고, 아직 기기가 안 붙은 토큰도 서명을
      // 실어 보내면 승인된 기기일 때 서버가 did를 달아 준다(observe 단계의 토큰 업그레이드).
      const claims = decodeTokenClaims(localStorage.getItem('authToken'));
      const device = claims?.affiliateId && claims?.userid
        ? await buildDeviceBlock('refresh', claims.affiliateId, String(claims.userid))
        : null;
      // 기기에 묶인 토큰인데 서명을 못 만들었으면(일시적인 네트워크 오류 등) 이번 갱신은 건너뛴다 —
      // 서명 없이 보내면 서버가 401을 주고 그 401이 로그아웃으로 이어진다(정책이 꺼졌으면 서명이 필요 없다)
      if (typeof claims?.did === 'number' && !device?.block && device?.mode !== 'off') return { refreshed: false };
      const data = await apiFetch<{
        success: boolean;
        token: string;
        user?: { avatar: string | null; notificationPrivacy?: NotificationPrivacy };
        device?: DeviceStatusInfo;
      }>(
        '/auth/refresh',
        { method: 'POST', body: JSON.stringify(device?.block ? { device: device.block } : {}) }
      );
      if (data?.success && data.token) {
        storeToken(data.token);
        return data.user !== undefined
          ? {
              refreshed: true,
              avatar: data.user.avatar ?? null,
              // 구 서버는 이 필드가 없다 → undefined 그대로 흘려 기존 값을 지킨다
              notificationPrivacy: data.user.notificationPrivacy,
            }
          : { refreshed: true };
      }
    } catch (error) {
      console.warn('[Auth] Token refresh failed (keeping current token):', error);
    }
    return { refreshed: false };
  },

  /**
   * 기기 키로 세션 재개 — 토큰이 만료된 승인 웹 기기가 비밀번호 없이 다시 들어온다.
   * 서버 조건: 승인된 웹 기기 · 마지막 사용이 재개 기간(기본 30일) 안 · 서명 · 로그인 승인.
   * 실패하면 { success:false } — 호출부가 로그인 화면으로 보낸다.
   */
  resumeSession: async (): Promise<LoginResponse> => {
    const who = loadResumeIdentity();
    // 재개 비밀값이 없으면 서버가 어차피 거부한다(로그인 시도 제한에도 세인다) — 보내지 않는다
    if (!who || !who.resumeSecret) return { success: false };
    try {
      const device = await buildDeviceBlock('resume', who.affiliateId, who.userid);
      if (!device?.block || device.block.kind !== 'web' || device.mode === 'off') return { success: false };
      const appVersion = await getAppVersion();
      const res = await fetch(`${API_URL}/auth/device-resume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...who, appVersion, clientType: getClientType(), device: device.block }),
      });
      const data: LoginResponse = await res.json().catch(() => ({ success: false }));
      if (data?.success && data.token) storeToken(data.token);
      return data;
    } catch {
      return { success: false };
    }
  },

  /** 토큰의 기기 묶음(did). 화면 분기용 — 판정은 서버 */
  tokenDeviceId: (): number | null => {
    const did = decodeTokenClaims(localStorage.getItem('authToken'))?.did;
    return typeof did === 'number' ? did : null;
  },

  /**
   * 직접 로그아웃을 서버에 알린다 — 이 기기의 재개 비밀값을 서버에서 지운다(감사 C9). best-effort라 실패해도
   * 로컬 정리(재개 신원 삭제)로 로그아웃은 성립한다.
   * ⚠ 토큰은 **첫 줄에서 동기로** 읽는다 — 호출부가 곧바로 로컬 토큰을 지운다.
   * ⚠ apiFetch를 쓰지 않는다 — 토큰이 만료돼 401이 오면 apiFetch가 auth:unauthorized를 발행하고, 그 처리기가
   *   기기 키 재개를 시도해 로그아웃 도중 다시 로그인될 수 있다.
   */
  logoutServer: async (): Promise<void> => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 4000);
    try {
      const who = loadResumeIdentity();
      // 토큰에 기기(did)가 없을 때를 대비해 이 브라우저 키의 지문도 싣는다(웹만 — PC 앱은 재개하지 않는다)
      const id = !isTauri() && who?.resumeSecret ? await getDeviceIdentity().catch(() => null) : null;
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(id?.kind === 'web' && id.keyFp ? { keyFp: id.keyFp } : {}),
        signal: controller.signal,
      });
    } catch {
      /* 오프라인·시간 초과 — 로컬 정리로 충분하다 */
    } finally {
      clearTimeout(timer);
    }
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('authToken');
  }
};
